import { defineStore, getActivePinia } from 'pinia'
import { ref, computed, watch } from 'vue'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { useRouter } from 'vue-router'
import createLogger from '@/utils/logger'
import { claimsRefreshSchema } from '@gh/common/models'
import { UserRole, type RoleEnum } from '@/types/roles'
import { useClassesStore } from './classes'
import { useSubscriptionStore } from './subscription'
import { useCustomerStore } from './customer'
import { useSignupStore } from './signup'
import { useProductsStore } from './products'
import { useCustomerConsultationsHubStore } from './customerConsultationsHub'
import { useCustomerClinicalConsultationPresenceStore } from './customerClinicalConsultationPresence'
import { useCustomerCarePlansStore } from './customerCarePlans'
import { useUserChoicesStore } from './userChoices'
import type { AuthUserId } from '@gh/common/models'
import { useTellescopeAvailabilityStore } from './tellescope'
import { useFirebaseMessagingStore } from './firebaseMessaging'
import { unregisterEvidenceServiceWorker } from '@/composables/useEvidenceProxy'
import { useAnalyticsStore } from './analytics'
import { analytics as analyticsUtil } from '@/utils/analytics'
import { removeCustomerScopedLocalStorage } from '@/utils/logoutStorageCleanup'
import { auth, firestore } from '@/firebase'
import { Admin } from '@/components/cloudFunctions/admin'
import type { RouteMetaCustom } from '@/router/types'
import { waitUntilReady } from '@/utils/storeUtils'
import { getErrorCode } from '@/utils/errorUtils'
import { isTransientFirebaseAuthTokenError } from '@/utils/firebaseAuthTransientErrors'
import { notifySwAuthClear, notifySwAuthSync } from '@/utils/swAuthSync'
import { resetClinicalBookingClientCaches } from '@/components/consultation/resetClinicalBookingClientCaches'

const logger = createLogger('stores/auth.ts')

/** Shown on the login screen when another tab (or this tab's auth listener) drops the session. */
const MSG_AUTH_DROPPED = 'You have been signed out.'

function loginPath(errorMessage?: string | null): string {
  if (!errorMessage) return '/login'
  return `/login?error=${encodeURIComponent(errorMessage)}`
}

// Constants
const GREATLY_HEALTH_EMAIL_DOMAIN = '@greatlyhealth.com'
type AuthUser = User & { uid: AuthUserId }

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null | undefined>(undefined)
  const isLoading = ref(true)
  /** Full-screen sign-out overlay only; not used for initial load or other store loading. */
  const isSigningOut = ref(false)
  const isAdmin = ref<boolean | null>(null) // null = not checked, true/false = checked
  const isInstructor = ref<boolean | null>(null) // null = not checked, true/false = checked
  const isCoach = ref<boolean | null>(null) // null = not checked, true/false = checked
  const isTestCustomer = ref<boolean | null>(null) // null = not checked, true/false = checked
  const allowPWA = ref<boolean | null>(null) // null = not checked, true/false = checked
  const unsubscribeAuth = ref<(() => void) | null>(null)
  const router = useRouter()
  const isAuthenticated = computed(() => !!user.value)
  let unsubscribeClaimsRefresh: (() => void) | null = null
  const lastClaimsRefreshTime = ref<Date | null>(null)
  const tokenIssuedAtTime = ref<Date | null>(null)
  /** Tracks previous user for login event: only track login when transitioning from no user to user */
  const previousUserForLogin = ref<AuthUser | null | undefined>(undefined)

  let claimsCorrelationDebouncedRefreshTimer: ReturnType<typeof setTimeout> | null = null
  /** Runs `refreshToken(true)` serially for claims_refresh-driven updates (debounced). */
  let claimsCorrelationRefreshSequential: Promise<void> = Promise.resolve()
  const CLAIMS_CORRELATION_DEBOUNCE_MS = 200

  watch(
    user,
    async (newUser) => {
      logger.log('watch user', newUser)
      const prev = previousUserForLogin.value
      const isNewSignIn =
        !!newUser &&
        (prev === null ||
          prev === undefined ||
          (typeof prev === 'object' && 'uid' in prev && prev.uid !== newUser.uid))
      if (newUser) {
        // Force-refresh on new sign-in (and first hydration: previousUserForLogin is initially
        // undefined) so ID tokens include blocking-hook sessionClaims (`ghStaffSid`) before
        // staff heartbeat writes to `staffSessions` (Firestore rules require claim + doc match).
        //
        // Exception: custom-token sign-in does not run blocking functions (Firebase docs).
        // Staff mint embeds `ghStaffSid` as a createCustomToken claim; force-refresh can drop
        // those developer claims and immediately invalidate the staff session.
        let forceRefresh = isNewSignIn
        if (isNewSignIn) {
          try {
            const cached = await newUser.getIdTokenResult(false)
            if (cached.signInProvider === 'custom') {
              forceRefresh = false
              logger.log(
                'Skipping force token refresh after custom-token sign-in to preserve developer claims',
              )
            }
          } catch (err) {
            logger.warn('Could not read sign-in provider before refresh; forcing refresh', err)
          }
        }
        await refreshToken(forceRefresh, isNewSignIn)
        // When claims are updated (e.g. via admin_setUserCustomClaims), backend writes to claims_refresh/{uid}
        unsubscribeClaimsRefresh?.()
        unsubscribeClaimsRefresh = onSnapshot(
          doc(firestore, 'claims_refresh', newUser.uid),
          (newDoc) => {
            const { success, data, error } = claimsRefreshSchema.safeParse({
              ...newDoc.data(),
              id: newDoc.id,
            })
            if (!success) {
              logger.error('Error parsing claims_refresh data', {
                error,
                data,
                snap: newDoc.data(),
              })
              return
            }
            const updatedAt = data.updatedAt
            if (updatedAt) {
              lastClaimsRefreshTime.value = new Date(updatedAt)
              logger.log('claims_refresh document updated at', updatedAt)
            }
          },
        )
      } else if (newUser === null) {
        unsubscribeClaimsRefresh?.()
        unsubscribeClaimsRefresh = null
        // onAuthStateChanged will be called with null when the user is not signed in, eg for login page.
        // A real session that drops in this tab (other tab signed out, token revoked) must leave
        // protected routes. The tab that called signOut() navigates itself.
        if (!isSigningOut.value) {
          const hadSession = prev != null && typeof prev === 'object' && 'uid' in prev
          if (hadSession) {
            await redirectToLoginAfterAuthDrop()
          }
          await afterSignOut()
        }
      }

      previousUserForLogin.value = newUser ?? null
      if (newUser !== undefined) {
        isLoading.value = false
      }
    },
    { immediate: true },
  )

  const isTestCustomerComputed = computed(() => {
    return isTestCustomer.value === true
  })

  const allowPWAComputed = computed(() => {
    return allowPWA.value === true
  })

  const isCustomer = computed(() => {
    if (isAdmin.value === null || isInstructor.value === null || isCoach.value === null) {
      return null
    }
    return !isAdmin.value && !isInstructor.value && !isCoach.value
  })

  const isSystemUser = computed((): boolean => {
    return isAdmin.value || isInstructor.value || isCoach.value || false
  })

  const userRole = computed((): RoleEnum | null => {
    if (isAdmin.value === null || isInstructor.value === null || isCoach.value === null) {
      return null
    }

    if (isAdmin.value) {
      return UserRole.ADMIN
    }

    if (isCoach.value) {
      return UserRole.COACH
    }

    if (isInstructor.value) {
      return UserRole.INSTRUCTOR
    }

    return UserRole.CUSTOMER
  })

  function untilRoleIsLoaded() {
    return waitUntilReady({ continueOnTrue: () => userRole.value !== null })
  }

  const userRoles = computed((): RoleEnum[] => {
    if (isAdmin.value === null || isInstructor.value === null || isCoach.value === null) {
      return []
    }

    const roles: RoleEnum[] = []

    if (isAdmin.value) {
      roles.push(UserRole.ADMIN)
    }

    if (isInstructor.value) {
      roles.push(UserRole.INSTRUCTOR)
    }

    if (isCoach.value) {
      roles.push(UserRole.COACH)
    }

    if (roles.length === 0) {
      roles.push(UserRole.CUSTOMER)
    }

    return roles
  })

  async function reloadUser() {
    if (!user.value) {
      return
    }
    await user.value.reload()
    await auth.currentUser?.reload()
    user.value = (await auth.currentUser) as AuthUser
    logger.log('reloadUser done', user.value)
    await refreshToken()
  }

  // Generic helper function to wait for a claim to be updated with exponential backoff
  async function waitForClaim(
    claimCheck: () => boolean,
    logIdentifier: string,
    maxAttempts = 4,
    initialDelay = 50,
  ): Promise<boolean> {
    logger.log(`waitFor${logIdentifier}ClaimUpdate`)
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Force refresh token to get latest custom claims
      await refreshToken(true)

      // Check if the claim is now active
      if (claimCheck()) {
        logger.log(`${logIdentifier} claim found on attempt ${attempt + 1}`)
        return true
      }

      // Exponential backoff: 50ms, 100ms, 200ms, 400ms
      const delay = initialDelay * 2 ** attempt
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
    logger.log(`${logIdentifier} claim not found after max attempts`)
    return false
  }

  // helper function to wait for system user claims to be updated
  // used after first sign-in for users with greatlyhealth.com email domain
  async function waitForSystemUserClaimUpdate(
    maxAttempts = 4,
    initialDelay = 50,
  ): Promise<boolean> {
    return waitForClaim(() => isSystemUser.value, 'SystemUser', maxAttempts, initialDelay)
  }

  // helper function to check if current user has greatlyhealth.com email domain
  function hasGreatlyHealthEmail(): boolean {
    return user.value?.email?.endsWith(GREATLY_HEALTH_EMAIL_DOMAIN) || false
  }

  async function refreshToken(forceRefresh = true, isNewSignIn = false) {
    logger.log('refreshToken', { forceRefresh, isNewSignIn })
    if (!user.value) {
      logger.log('refreshToken no user')
      return
    }
    const t0 = performance.now()
    const previousIssuedAtMs = tokenIssuedAtTime.value?.getTime() ?? null
    let shouldNotifySwAuthSync = false
    try {
      // Force token refresh to get latest custom claims
      const token = await user.value.getIdTokenResult(forceRefresh)
      logger.log('Token info:', {
        claims: token.claims,
        authTime: token.authTime,
        expirationTime: token.expirationTime,
        issuedAtTime: token.issuedAtTime,
        signInProvider: token.signInProvider,
        signInSecondFactor: token.signInSecondFactor,
      })
      isAdmin.value = token.claims.admin === true
      logger.log(`Admin claim check result: ${isAdmin.value}`)

      isInstructor.value = token.claims.instructor === true
      logger.log(`Instructor claim check result: ${isInstructor.value}`)

      isCoach.value = token.claims.coach === true
      logger.log(`Coach claim check result: ${isCoach.value}`)

      isTestCustomer.value = token.claims.testCustomer === true
      logger.log(`Test customer claim check result: ${isTestCustomer.value}`)

      allowPWA.value = token.claims.allowPWA === true
      logger.log(`Allow PWA claim check result: ${allowPWA.value}`)

      if (isTestCustomer.value) {
        analyticsUtil.setDebugMode(true).catch((err) => {
          logger.error('Failed to set Analytics debug mode', err)
        })
      }

      // Analytics user ID and properties are set by analyticsStore watchers
      // Track login event only on actual sign-in (transition from no user to user), not on every token refresh
      if (isNewSignIn) {
        void useAnalyticsStore().events.trackLogin({
          method: token.signInProvider || 'unknown',
          user_role: userRole.value || 'customer',
        })
      }
      const newIssuedAt = new Date(token.issuedAtTime)
      const newIssuedAtMs = newIssuedAt.getTime()
      tokenIssuedAtTime.value = newIssuedAt
      logger.log(`Token issued at time: ${tokenIssuedAtTime.value}`)
      shouldNotifySwAuthSync = isNewSignIn || previousIssuedAtMs !== newIssuedAtMs
    } catch (error) {
      console.error('Error checking admin claim:', error)
      if (isTransientFirebaseAuthTokenError(error)) {
        logger.warn('Transient Firebase auth token error; preserving role claim state', {
          code: getErrorCode(error),
        })
        return
      }
      if (!user.value) return
      isAdmin.value = false
      isInstructor.value = false
      isCoach.value = false
      isTestCustomer.value = false
      allowPWA.value = false
    }
    const t1 = performance.now()
    logger.log(`refreshToken took ${t1 - t0}ms`)
    if (user.value && shouldNotifySwAuthSync) {
      const issuedAtMs = tokenIssuedAtTime.value?.getTime()
      if (issuedAtMs != null) {
        void notifySwAuthSync({ uid: user.value.uid, issuedAtMs })
      }
    }
  }

  async function signOut(
    options: {
      redirectToLogin?: boolean
      errorMessage?: string | null
      hardReloadToLogin?: boolean
    } = {
      redirectToLogin: true,
      errorMessage: null,
      hardReloadToLogin: false,
    },
  ) {
    isSigningOut.value = true
    const wait1Second = new Promise((resolve) =>
      setTimeout(resolve, options.hardReloadToLogin ? 10 : 1 * 1000),
    )

    try {
      await beforeSignOut()

      if (isSystemUser.value && user.value) {
        try {
          await Admin.endStaffSession({})
        } catch (err) {
          logger.warn('Staff session end failed; continuing local sign-out', err)
        }
      }

      await auth.signOut()

      await afterSignOut()

      // Wait 1 second before redirecting to login, this is to ensure the user has time to see the signing out overlay
      await wait1Second

      if (options.redirectToLogin) {
        const path = loginPath(options.errorMessage)
        if (options.hardReloadToLogin && typeof window !== 'undefined') {
          window.location.assign(path)
          return
        }
        await router.replace(path)
      }
    } finally {
      isSigningOut.value = false
    }
  }

  function init() {
    unsubscribeAuth.value =
      unsubscribeAuth.value ||
      onAuthStateChanged(auth, async (newUser) => {
        logger.log('onAuthStateChanged', newUser)
        user.value = newUser as AuthUser
      })
  }

  /**
   * Other tabs share IndexedDB auth. When this tab's user disappears without a local
   * signOut(), leave protected pages for the login screen instead of staying on the shell.
   */
  async function redirectToLoginAfterAuthDrop(): Promise<void> {
    const route = router.currentRoute.value
    const requiresAuth = route.matched.some(
      (record) => (record.meta as RouteMetaCustom).requiresAuth === true,
    )
    if (!requiresAuth || route.path === '/login') {
      return
    }
    const path = loginPath(MSG_AUTH_DROPPED)
    try {
      await router.replace(path)
    } catch (err) {
      logger.warn('Router replace to login failed after auth drop', err)
      if (typeof window !== 'undefined') {
        window.location.assign(path)
      }
    }
  }

  async function beforeSignOut() {
    await useFirebaseMessagingStore().beforeSignOut()
  }

  async function afterSignOut() {
    logger.log('clear start')
    clearClaimsCorrelationDebouncedRefresh()
    claimsCorrelationRefreshSequential = Promise.resolve()
    const customerIdForStorage = useCustomerStore().customer?.id
    removeCustomerScopedLocalStorage(customerIdForStorage)

    unsubscribeClaimsRefresh?.()
    unsubscribeClaimsRefresh = null

    useFirebaseMessagingStore().signOut()
    useSignupStore().signOut()
    useSubscriptionStore().signOut()
    useClassesStore().signOut()
    useProductsStore().signOut()
    useCustomerConsultationsHubStore().signOut()
    useCustomerClinicalConsultationPresenceStore().signOut()
    useCustomerCarePlansStore().signOut()
    useUserChoicesStore().signOut()
    useCustomerStore().signOut()
    useTellescopeAvailabilityStore().signOut()

    try {
      resetClinicalBookingClientCaches()
    } catch (err) {
      logger.error('Failed to reset clinical booking client caches on sign-out', err)
    }

    const piniaState = getActivePinia()?.state.value
    if (piniaState?.clinicalConsultationBooking != null) {
      try {
        const { useClinicalConsultationBookingStore } =
          await import('./clinicalConsultationBooking')
        useClinicalConsultationBookingStore().signOut()
      } catch (err) {
        logger.error('Failed to sign out clinical consultation booking store', err)
      }
    }
    if (piniaState?.clinicalTellescopeAvailability != null) {
      try {
        const { useClinicalTellescopeAvailabilityStore } =
          await import('./clinicalTellescopeAvailability')
        useClinicalTellescopeAvailabilityStore().signOut()
      } catch (err) {
        logger.error('Failed to sign out clinical tellescope availability store', err)
      }
    }

    // Reset Remote Config immediately so the app doesn't show stale user config.
    // Do NOT chain this after async analytics clearing; that can race with a quick re-login
    // and end up resetting Remote Config after the user is already signed back in.
    const analyticsStore = useAnalyticsStore()

    await Promise.all([
      // Clear Analytics user (best-effort). Analytics store also clears itself on logout via its watcher.
      analyticsStore.clearUser().catch((err) => {
        logger.error('Failed to clear Analytics user', err)
      }),
      // Track logout event
      analyticsStore.events.trackLogout(),
      // Cleanup Evidence service worker and caches
      unregisterEvidenceServiceWorker().catch((err) => {
        logger.error('Failed to unregister Evidence service worker', err)
      }),
    ])

    isAdmin.value = null
    isInstructor.value = null
    isCoach.value = null
    isTestCustomer.value = null
    lastClaimsRefreshTime.value = null
    tokenIssuedAtTime.value = null
    notifySwAuthClear()
    logger.log('clear done')
  }

  function onUnmounted() {
    if (unsubscribeAuth.value) {
      unsubscribeAuth.value()
    }
  }

  function untilLoadingIsDone() {
    return waitUntilReady({ continueOnTrue: () => !isLoading.value })
  }

  function clearClaimsCorrelationDebouncedRefresh() {
    if (claimsCorrelationDebouncedRefreshTimer !== null) {
      clearTimeout(claimsCorrelationDebouncedRefreshTimer)
      claimsCorrelationDebouncedRefreshTimer = null
    }
  }

  function scheduleDebouncedSequentialClaimsCorrelationRefresh() {
    if (claimsCorrelationDebouncedRefreshTimer !== null) {
      clearTimeout(claimsCorrelationDebouncedRefreshTimer)
    }
    claimsCorrelationDebouncedRefreshTimer = setTimeout(() => {
      claimsCorrelationDebouncedRefreshTimer = null
      claimsCorrelationRefreshSequential = claimsCorrelationRefreshSequential
        .then(async () => {
          if (
            lastClaimsRefreshTime.value &&
            tokenIssuedAtTime.value &&
            lastClaimsRefreshTime.value > tokenIssuedAtTime.value
          ) {
            await refreshToken(true, false)
          }
        })
        .catch((err: unknown) => {
          logger.error('Claims-correlation sequential refreshToken failed', err)
        })
    }, CLAIMS_CORRELATION_DEBOUNCE_MS)
  }

  watch(
    [lastClaimsRefreshTime, tokenIssuedAtTime],
    ([newLastClaimsRefreshTime, newTokenIssuedAtTime]) => {
      if (
        newLastClaimsRefreshTime &&
        newTokenIssuedAtTime &&
        newLastClaimsRefreshTime > newTokenIssuedAtTime
      ) {
        logger.warn(
          'lastClaimsRefreshTime is greater than tokenIssuedAtTime, scheduling debounced token refresh',
        )
        scheduleDebouncedSequentialClaimsCorrelationRefresh()
      }
    },
    { immediate: true },
  )

  return {
    user,
    isLoading,
    isSigningOut,
    isCustomer,
    isAuthenticated,
    isAdmin,
    isInstructor,
    isCoach,
    userRole,
    userRoles,
    isTestCustomer: isTestCustomerComputed,
    allowPWA: allowPWAComputed,
    init,
    onUnmounted,
    signOut,
    unsubscribeAuth,
    isSystemUser,
    reloadUser,
    waitForSystemUserClaimUpdate,
    refreshToken,
    hasGreatlyHealthEmail,
    untilLoadingIsDone,
    untilRoleIsLoaded,
    tokenIssuedAtTime,
  }
})
